import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { transferService } from '../../services/transferService';
import { EyeIcon, PhoneIcon } from '@heroicons/react/24/outline';

const CarrierTransfersList = () => {
  const { t } = useTranslation();
  const [transfers, setTransfers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: '',
    date: '',
    searchTerm: '',
    page: 1,
    limit: 10
  });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    total: 0,
    limit: 10
  });

  // Quick action states
  const [quickActionLoading, setQuickActionLoading] = useState({});
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [selectedTransfer, setSelectedTransfer] = useState(null);

  useEffect(() => {
    fetchTransfers();
  }, [filters]);

  const fetchTransfers = async () => {
    setIsLoading(true);
    try {
      const response = await transferService.getAllTransfers(filters);

      if (response && response.success && response.data) {
        setTransfers(response.data.transfers || []);
        setPagination(response.data.pagination || {
          currentPage: parseInt(filters.page),
          totalPages: Math.ceil((response.data.total || 0) / filters.limit),
          total: response.data.total || 0,
          limit: parseInt(filters.limit)
        });
      } else {
        setTransfers([]);
        setPagination({
          currentPage: 1,
          totalPages: 1,
          total: 0,
          limit: parseInt(filters.limit)
        });
      }
    } catch (error) {
      toast.error('Transferler yüklenirken bir hata oluştu');
      console.error('Error fetching transfers:', error);
      setTransfers([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value,
      page: 1 // Reset page when filter changes
    }));
  };

  const handlePageChange = (newPage) => {
    setFilters(prev => ({
      ...prev,
      page: newPage
    }));
  };

  // Quick Actions
  const handleStatusChange = async (transferId, newStatus) => {
    setQuickActionLoading(prev => ({ ...prev, [`status_${transferId}`]: true }));
    try {
      const response = await transferService.updateStatus(transferId, newStatus);
      if (response?.success) {
        setTransfers(prev => prev.map(transfer =>
          transfer.id === transferId ? { ...transfer, status: newStatus } : transfer
        ));
        toast.success('Durum başarıyla güncellendi');
        setShowStatusModal(false);
        setSelectedTransfer(null);
      } else {
        toast.error('Durum güncellenirken bir hata oluştu');
      }
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Durum güncellenirken bir hata oluştu');
    } finally {
      setQuickActionLoading(prev => ({ ...prev, [`status_${transferId}`]: false }));
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('tr-TR');
  };

  const formatTime = (timeString) => {
    if (!timeString) return '-';
    return timeString.substring(0, 5);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-gray-100 text-gray-800';
      case 'confirmed':
        return 'bg-blue-100 text-blue-800';
      case 'vehicle_assigned':
        return 'bg-indigo-100 text-indigo-800';
      case 'on_the_way_to_pickup':
        return 'bg-yellow-100 text-yellow-800';
      case 'arrived_at_pickup':
        return 'bg-orange-100 text-orange-800';
      case 'passenger_on_board':
        return 'bg-purple-100 text-purple-800';
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
      case 'cancelled_by_customer':
      case 'cancelled_by_operator':
      case 'cancelled_by_driver':
        return 'bg-red-100 text-red-800';
      case 'no_show':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pending':
        return 'Beklemede';
      case 'confirmed':
        return 'Onaylandı';
      case 'vehicle_assigned':
        return 'Araç Atandı';
      case 'on_the_way_to_pickup':
        return 'Yolda';
      case 'arrived_at_pickup':
        return 'Alış Noktasında';
      case 'passenger_on_board':
        return 'Yolcu Alındı';
      case 'completed':
        return 'Tamamlandı';
      case 'cancelled':
        return 'İptal Edildi';
      case 'cancelled_by_customer':
        return 'Müşteri İptali';
      case 'cancelled_by_operator':
        return 'Operatör İptali';
      case 'cancelled_by_driver':
        return 'Şoför İptali';
      case 'no_show':
        return 'Gelmedi';
      default:
        return status;
    }
  };

  return (
    <div className="w-full max-w-none px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold text-gray-900">Transferler</h1>
          <p className="mt-2 text-sm text-gray-700">
            Tüm transfer işlemlerini görüntüleyin ve yönetin.
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="mt-6 bg-white shadow rounded-lg p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Durum</label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="">Tüm Durumlar</option>
              <option value="pending">Beklemede</option>
              <option value="ready">Hazır</option>
              <option value="in_progress">Devam Ediyor</option>
              <option value="completed">Tamamlandı</option>
              <option value="cancelled">İptal Edildi</option>
              <option value="no_show">Gelmedi</option>
              <option value="failed">Başarısız</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Tarih</label>
            <input
              type="date"
              value={filters.date}
              onChange={(e) => handleFilterChange('date', e.target.value)}
              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Arama</label>
            <input
              type="text"
              placeholder="Rezervasyon no, yolcu adı, uçuş no, otel adı..."
              value={filters.searchTerm}
              onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Sayfa Başına</label>
            <select
              value={filters.limit}
              onChange={(e) => handleFilterChange('limit', parseInt(e.target.value))}
              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="mt-6 bg-white shadow rounded-lg overflow-hidden">
        {isLoading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-indigo-600"></div>
          </div>
        ) : transfers.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">Henüz transfer bulunmuyor.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Durum
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tarihi & Saati
                  </th>
                  
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Uçak/Oda
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Yön
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Müşteri
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Müşt. Alınacak
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Taşıyıcı
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Araç
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Komisyon
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    İşlemler
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Rezervasyon
                  </th>
                  
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {transfers.map((transfer) => {
                  const statusBgColor = {
                    'pending': 'bg-gray-50',
                    'confirmed': 'bg-blue-50',
                    'vehicle_assigned': 'bg-indigo-50',
                    'on_the_way_to_pickup': 'bg-yellow-50',
                    'arrived_at_pickup': 'bg-orange-50',
                    'passenger_on_board': 'bg-purple-50',
                    'completed': 'bg-green-50',
                    'cancelled': 'bg-red-50',
                    'cancelled_by_customer': 'bg-red-50',
                    'cancelled_by_operator': 'bg-red-50',
                    'cancelled_by_driver': 'bg-red-50',
                    'no_show': 'bg-orange-50'
                  }[transfer.status] || 'bg-gray-50';
                  
                  return (
                    <tr key={transfer.id} className={statusBgColor}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => {
                            setSelectedTransfer(transfer);
                            setShowStatusModal(true);
                          }}
                          className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full hover:opacity-80 transition-opacity ${getStatusColor(transfer.status)}`}
                        >
                          {getStatusText(transfer.status)}
                        </button>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(transfer.transfer_date)} {formatTime(transfer.transfer_time)}
                      </td>
                  

                     <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                       {[transfer.flight_number, transfer.room_number]
                         .filter(Boolean)
                         .join(' / ')}
                     </td>

                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {transfer.is_return ? 'Dönüş' : 'Gidiş'}
                        <br />
                        {transfer.reservation?.fromRegion?.name || '-'} → {transfer.reservation?.toRegion?.name || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div className="flex items-center space-x-2">
                          <div>
                            <div className="text-gray-700 font-medium">
                              {transfer.reservation?.passengers?.[0]?.first_name || ''} {transfer.reservation?.passengers?.[0]?.last_name || ''}
                            </div>
                            <div className="text-xs text-gray-500">
                              Toplam {transfer.reservation?.passenger_count || transfer.reservation?.passengers?.length || 0} yolcu
                            </div>
                          </div>
                          {transfer.reservation?.contactDetails?.phone && (
                            <a
                              href={`tel:${transfer.reservation.contactDetails.phone.replace(/[^0-9]/g, '')}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center p-1 bg-green-500 hover:bg-green-600 text-white rounded-full transition-colors"
                              title="WhatsApp ile iletişime geç"
                            >
                              <PhoneIcon className="h-3 w-3" />
                            </a>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {transfer?.collection_amount
                          ? `${transfer.collection_amount} ${transfer.collection_currency || ''}`

                          : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {transfer.carrier?.name || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {transfer.vehicle?.plate_number || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {transfer.commission_amount > 0 ? (
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {transfer.commission_amount} {transfer.commission_currency}
                            </div>
                            <div className={`text-xs ${
                              transfer.commission_status === 'paid' ? 'text-green-600' :
                              transfer.commission_status === 'approved' ? 'text-blue-600' :
                              transfer.commission_status === 'cancelled' ? 'text-red-600' :
                              'text-yellow-600'
                            }`}>
                              {transfer.commission_status === 'paid' ? 'Ödendi' :
                               transfer.commission_status === 'approved' ? 'Onaylandı' :
                               transfer.commission_status === 'cancelled' ? 'İptal' :
                               'Beklemede'}
                            </div>
                          </div>
                        ) : (
                          <span className="text-gray-400">Komisyon yok</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end space-x-2">
                          <Link
                            to={`/reservations/${transfer.reservation_id}`}
                            className="inline-flex items-center p-1.5 border border-transparent rounded-full shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                            title="Detaylar"
                          >
                            <EyeIcon className="h-4 w-4" aria-hidden="true" />
                          </Link>
                        </div>
                      </td>
                       <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                       {transfer.reservation?.reservation_number || '-'}
                       <br />
                       {transfer.reservation?.agency?.name || '-'}
                     </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {!isLoading && transfers.length > 0 && (
        <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6 mt-4 rounded-lg shadow">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={() => handlePageChange(pagination.currentPage - 1)}
              disabled={pagination.currentPage === 1}
              className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 ${
                pagination.currentPage === 1 ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              Önceki
            </button>
            <button
              onClick={() => handlePageChange(pagination.currentPage + 1)}
              disabled={pagination.currentPage === pagination.totalPages}
              className={`ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 ${
                pagination.currentPage === pagination.totalPages ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              Sonraki
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                <span className="font-medium">{((pagination.currentPage - 1) * pagination.limit) + 1}</span>
                {' - '}
                <span className="font-medium">
                  {Math.min(pagination.currentPage * pagination.limit, pagination.total)}
                </span>
                {' / '}
                <span className="font-medium">{pagination.total}</span>
                {' ' + t('pagination.resultsShowing', { total: pagination.total })}
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                <button
                  onClick={() => handlePageChange(pagination.currentPage - 1)}
                  disabled={pagination.currentPage === 1}
                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                >
                  {t('pagination.previous')}
                </button>
                
                {/* Page numbers */}
                {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                  const pageNumber = Math.max(1, pagination.currentPage - 2) + i;
                  if (pageNumber <= pagination.totalPages) {
                    return (
                      <button
                        key={pageNumber}
                        onClick={() => handlePageChange(pageNumber)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          pageNumber === pagination.currentPage
                            ? 'z-10 bg-indigo-50 border-indigo-500 text-indigo-600'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        {pageNumber}
                      </button>
                    );
                  }
                  return null;
                })}
                
                <button
                  onClick={() => handlePageChange(pagination.currentPage + 1)}
                  disabled={pagination.currentPage === pagination.totalPages}
                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                >
                  {t('pagination.next')}
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}

      {/* Status Update Modal */}
      {showStatusModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">{t('modal.statusUpdate')}</h3>
              <p className="text-sm text-gray-500 mb-4">
                  {t('modal.transfer')}: {selectedTransfer?.reservation?.reservation_number} - {selectedTransfer?.is_return ? t('modal.return') : t('modal.arrival')}
                </p>
              <div className="space-y-3">
                {[
                  { value: 'on_the_way_to_pickup', label: t('transferStatus.on_the_way_to_pickup'), color: 'bg-yellow-100 text-yellow-800' },
                  { value: 'arrived_at_pickup', label: t('transferStatus.arrived_at_pickup'), color: 'bg-orange-100 text-orange-800' },
                  { value: 'passenger_on_board', label: t('transferStatus.passenger_on_board'), color: 'bg-purple-100 text-purple-800' },
                  { value: 'cancelled_by_driver', label: t('transferStatus.cancelled_by_driver'), color: 'bg-red-100 text-red-800' },
                  { value: 'no_show', label: t('transferStatus.no_show'), color: 'bg-orange-100 text-orange-800' }
                ].map(status => (
                  <button
                    key={status.value}
                    onClick={() => handleStatusChange(selectedTransfer.id, status.value)}
                    disabled={quickActionLoading[`status_${selectedTransfer?.id}`]}
                    className={`w-full text-left p-3 border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 ${
                      selectedTransfer?.status === status.value ? 'ring-2 ring-indigo-500 bg-indigo-50' : ''
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{status.label}</span>
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${status.color}`}>
                        {status.label}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => {
                    setShowStatusModal(false);
                    setSelectedTransfer(null);
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  {t('common.cancel')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CarrierTransfersList;
